import React, { useState, useEffect } from "react";
import {
  magazineFeatures as mockMagazineFeatures,
  insights as mockInsights,
  magazineIssue as mockMagazineIssue,
  caseStudies as mockCaseStudies,
  podcasts as mockPodcasts,
  magazineTopics as mockMagazineTopics,
} from "../mock";
import { ArrowRight, Headphones, BookOpen } from "lucide-react";
import { getMagazine, getInsights } from "../api";
import { CaseStudyCard } from "../components/magazine/CaseStudyCard";
import { PodcastRow } from "../components/magazine/PodcastRow";
import { useI18n } from "../i18n/I18nContext";

const Magazine = () => {
  const { t, term, localize } = useI18n();
  const [magazineFeatures, setMagazineFeatures] = useState(mockMagazineFeatures);
  const [insights, setInsights] = useState(mockInsights);
  const [magazineIssue, setMagazineIssue] = useState(mockMagazineIssue);
  const [rawCaseStudies, setCaseStudies] = useState(mockCaseStudies);
  const [podcasts, setPodcasts] = useState(mockPodcasts);
  const [magazineTopics, setMagazineTopics] = useState(mockMagazineTopics);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    getMagazine()
      .then((d) => {
        if (d?.features?.length) setMagazineFeatures(d.features);
        if (d?.caseStudies?.length) setCaseStudies(d.caseStudies);
        if (d?.podcasts?.length) setPodcasts(d.podcasts);
        if (d?.topics?.length) setMagazineTopics(d.topics);
        if (d?.issue) setMagazineIssue(d.issue);
      })
      .catch(() => {});
    getInsights().then((d) => Array.isArray(d) && d.length && setInsights(d)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const caseStudies = localize("case_studies", rawCaseStudies);
  const lead = magazineFeatures[0];
  const secondary = magazineFeatures.slice(1);

  return (
    <div className="bg-white">
      {/* Masthead topic bar (HBR-style) */}
      <div className="border-b border-slate-200 bg-white sticky top-16 md:top-20 z-40">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="flex items-center justify-between h-14 overflow-x-auto">
            <div className="flex items-center gap-7">
              <span className="font-display font-800 text-[#0a2540] whitespace-nowrap">
                {t("bpcMagazineLabel")}
              </span>
              <nav className="hidden md:flex items-center gap-6">
                {magazineTopics.map((topic) => (
                  <a
                    key={topic}
                    href="#"
                    className="text-sm font-500 text-slate-600 hover:text-[#0a2540] whitespace-nowrap link-underline"
                  >
                    {term(topic)}
                  </a>
                ))}
              </nav>
            </div>
            <button className="hidden sm:inline-flex items-center gap-2 bg-[#0a2540] hover:bg-[#16355c] text-white text-sm font-600 px-5 py-2 rounded-full transition-colors whitespace-nowrap">
              {t("subscribe")}
            </button>
          </div>
        </div>
      </div>

      {/* Featured + Issue sidebar */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Lead story */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="relative overflow-hidden rounded-lg aspect-[4/3] bg-slate-100 hover-zoom group order-1 md:order-none">
                <img src={lead.image} alt={lead.title} className="w-full h-full object-cover" />
              </div>
              <div>
                <span className="text-xs font-700 uppercase tracking-wider text-[#0a6ed1]">
                  {t("coverStory")}
                </span>
                <h1 className="font-display font-800 text-3xl md:text-4xl text-[#0a2540] mt-3 leading-[1.1]">
                  {lead.title}
                </h1>
                <p className="mt-4 text-slate-500 text-[16px] leading-relaxed">
                  {lead.excerpt}
                </p>
                <div className="mt-4 text-sm text-slate-400">
                  {t("byEditors")}
                </div>
              </div>
            </div>

            <div className="mt-12 pt-10 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-10">
              {secondary.map((m) => (
                <div key={m.id} className="group hover-zoom">
                  <div className="relative overflow-hidden rounded-lg aspect-[16/10] bg-slate-100">
                    <img src={m.image} alt={m.title} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <span className="mt-4 inline-block text-xs font-700 uppercase tracking-wider text-[#0a6ed1]">
                    {m.issue}
                  </span>
                  <h3 className="mt-2 font-display font-700 text-xl text-[#0a2540] group-hover:text-[#0a6ed1] transition-colors">
                    {m.title}
                  </h3>
                  <p className="mt-2 text-slate-500 text-[15px]">{m.excerpt}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Issue cover sidebar */}
          <aside className="lg:col-span-4">
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 lg:sticky lg:top-36">
              <span className="text-xs font-700 uppercase tracking-wider text-[#0a6ed1]">
                {t("currentIssue")}
              </span>
              <div className="mt-4 relative overflow-hidden rounded-lg aspect-[3/4] bg-slate-200 shadow-md">
                <img src={magazineIssue.cover} alt={magazineIssue.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a2540]/80 to-transparent" />
                <div className="absolute bottom-0 p-5 text-white">
                  <div className="text-xs font-600 uppercase tracking-wider text-slate-200">
                    {magazineIssue.label}
                  </div>
                  <div className="font-display font-800 text-2xl leading-tight">
                    {magazineIssue.title}
                  </div>
                </div>
              </div>
              <p className="mt-5 text-slate-500 text-[15px] leading-relaxed">
                {magazineIssue.blurb}
              </p>
              <button className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#0a2540] hover:bg-[#16355c] text-white font-600 px-6 py-3 rounded-full transition-colors">
                {t("subscribeToBpc")} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </aside>
        </div>
      </section>

      {/* Latest list (HBR "Latest" column) */}
      <section className="bg-slate-50 py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-8 h-0.5 bg-[#0a2540]" />
            <h2 className="font-display font-800 text-2xl md:text-3xl text-[#0a2540]">
              {t("latestFromMag")}
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-14">
            {insights.slice(0, 6).map((i, idx) => (
              <div
                key={i.id}
                className="group flex items-center gap-6 py-6 border-b border-slate-200 cursor-pointer"
              >
                <span className="font-display font-800 text-2xl text-slate-300 w-8">
                  {idx + 1}
                </span>
                <div className="flex-1">
                  <span className="text-xs font-700 uppercase tracking-wider text-[#0a6ed1]">
                    {i.category}
                  </span>
                  <h3 className="font-display font-700 text-lg text-[#0a2540] group-hover:text-[#0a6ed1] transition-colors">
                    {i.title}
                  </h3>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-[#0a6ed1] group-hover:translate-x-1 transition-all" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies (case selections) */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="flex items-end justify-between mb-10">
          <div className="flex items-center gap-3">
            <BookOpen className="w-6 h-6 text-[#0a6ed1]" />
            <div>
              <span className="text-sm font-700 uppercase tracking-widest text-[#0a6ed1]">
                {t("caseSelections")}
              </span>
              <h2 className="font-display font-800 text-2xl md:text-3xl text-[#0a2540]">
                {t("caseStudiesTitle")}
              </h2>
            </div>
          </div>
          <a href="#" className="hidden md:inline-flex items-center gap-2 text-[#0a2540] font-600 hover:text-[#0a6ed1] transition-colors">
            {t("browseAll")} <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {caseStudies.map((cs) => (
            <CaseStudyCard key={cs.id} item={cs} />
          ))}
        </div>
      </section>

      {/* Podcasts */}
      <section className="bg-[#0a2540] text-white py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3 mb-4">
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[#0a6ed1]">
                  <Headphones className="w-6 h-6 text-white" />
                </span>
                <span className="text-sm font-700 uppercase tracking-widest text-[#5eb0f5]">
                  {t("bpcPodcasts")}
                </span>
              </div>
              <h2 className="font-display font-800 text-3xl md:text-4xl leading-tight">
                {t("podcastsTitle")}
              </h2>
              <p className="mt-5 text-slate-300 text-[16px] leading-relaxed">
                {t("podcastsBody")}
              </p>
              <button className="mt-7 inline-flex items-center gap-2 bg-white text-[#0a2540] hover:bg-slate-100 font-600 px-6 py-3 rounded-full transition-colors">
                {t("allEpisodes")} <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            <div className="lg:col-span-8">
              <div className="divide-y divide-white/10 border-t border-white/10">
                {podcasts.map((p) => (
                  <PodcastRow
                    key={p.id}
                    podcast={p}
                    isPlaying={playing === p.id}
                    onToggle={setPlaying}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Magazine;
